import { Link, Navigate, useNavigate } from "react-router-dom";
import { Show, useAuth } from "@clerk/react";
import { MapPin, ImageOff, Heart } from "lucide-react";
import { toast } from "sonner";
import { photoUrl, eur, STATUS_LABELS } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export function RequireAuth({ children, roles }) {
  return (
    <>
      <Show when="signed-in"><RoleGate roles={roles}>{children}</RoleGate></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}

function RoleGate({ roles, children }) {
  const { me } = useMe();
  if (!roles) return children;
  if (!me) return <PageShell><p className="text-stone-500">Chargement…</p></PageShell>;
  if (!roles.includes(me.role)) return <PageShell><p data-testid="forbidden-msg" className="text-stone-600">Cet espace est réservé ({roles.join(", ")}). Demandez l'accès depuis votre compte.</p></PageShell>;
  return children;
}

export function PageShell({ title, eyebrow, actions, children }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 fade-up">
      {(title || eyebrow) && (
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
            {title && <h1 className="font-serif text-4xl sm:text-5xl text-forest tracking-tight leading-none">{title}</h1>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function Photo({ src, alt, className = "" }) {
  const url = photoUrl(src);
  if (!url) return <div className={`bg-muted flex items-center justify-center text-stone-400 ${className}`}><ImageOff className="w-8 h-8" aria-hidden="true" /></div>;
  return <img src={url} alt={alt} loading="lazy" className={`object-cover ${className}`} />;
}

export function Pill({ children, tone = "stone", testid }) {
  const tones = {
    stone: "bg-stone-100 text-stone-700", forest: "bg-forest text-sand", terra: "bg-terra text-white",
    sand: "bg-muted text-forest", warn: "bg-amber-100 text-amber-800", red: "bg-red-100 text-red-700", green: "bg-emerald-100 text-emerald-800",
  };
  return <span data-testid={testid} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tones[tone]}`}>{children}</span>;
}

export function StatusPill({ status, testid }) {
  const tone = ["active", "accepted", "paid", "completed", "delivered", "approved", "resolved", "confirmed"].includes(status) ? "green"
    : ["rejected", "cancelled", "suspended", "refused", "expired", "refunded"].includes(status) ? "red"
    : ["pending", "pending_payment", "needs_review", "appeal", "open", "recipient_absent"].includes(status) ? "warn" : "sand";
  return <Pill tone={tone} testid={testid}>{STATUS_LABELS[status] || status}</Pill>;
}

function FavButton({ id }) {
  const { favIds, toggleFav } = useMe();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const on = favIds.has(id);
  const click = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) return navigate("/sign-in");
    try { toast.success((await toggleFav(id)) ? "Ajouté aux favoris" : "Retiré des favoris"); } catch { toast.error("Action impossible"); }
  };
  return (
    <button data-testid={`card-favorite-${id}`} onClick={click} aria-label="Favori" className="absolute top-3 right-3 rounded-full bg-white/90 p-2 shadow-sm hover:scale-110 transition-transform">
      <Heart className={`w-4 h-4 ${on ? "fill-forest text-forest" : "text-stone-600"}`} />
    </button>
  );
}

export function PriceBreakdown({ l }) {
  const p = l.pricing;
  if (!p) return null;
  const row = "flex justify-between py-1.5 text-sm";
  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-5" data-testid="price-breakdown">
      <div className={row}><span>{l.type === "sale" ? "Prix demandé par le vendeur" : "Objet donné"}</span><span data-testid="pb-seller-price">{eur(p.seller_price)}</span></div>
      <div className={row}><span>Frais labradedie.fr ({p.fee_label})</span><span data-testid="pb-fee">+ {eur(p.platform_fee)}</span></div>
      <div className={`${row} font-semibold border-t border-stone-100 mt-1 pt-2`}><span>Total remise en main propre</span><span data-testid="pb-buyer-price">{eur(p.buyer_price)}</span></div>
      <div className={row}><span>Frais de livraison (facultatif)</span><span data-testid="pb-delivery">{p.delivery_from != null ? `+ dès ${eur(p.delivery_from)}` : "non éligible"}</span></div>
      {p.delivery_from != null && <div className={`${row} text-terra font-semibold`}><span>Total avec livraison</span><span data-testid="pb-total-delivery">dès {eur(p.buyer_price + p.delivery_from)}</span></div>}
      <p className="text-[11px] text-stone-500 mt-1">Frais payés par l'acheteur. Livraison exacte calculée selon la destination et le transporteur (Mondial Relay, Chronopost, coursier).</p>
    </div>
  );
}

export function ListingCard({ l, index = 0 }) {
  return (
    <Link to={`/annonce/${l.id}`} data-testid={`listing-card-${l.id}`} className="group block bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 fade-up" style={{ animationDelay: `${index * 50}ms` }}>
      <div className="relative aspect-[4/5] overflow-hidden">
        <Photo src={l.photos?.[0]} alt={l.title} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 flex gap-2">
          {l.type === "donation" ? <Pill tone="terra">Don</Pill> : <Pill tone="forest">{eur(l.price)}</Pill>}
          {l.status !== "active" && <Pill tone="warn">{STATUS_LABELS[l.status]}</Pill>}
        </div>
        {l.id !== "preview" && <FavButton id={l.id} />}
      </div>
      <div className="p-4">
        <p className="font-medium text-stone-900 line-clamp-1">{l.title}</p>
        {l.pricing && (
          <p className="text-xs text-terra mt-0.5" data-testid={`card-buyer-price-${l.id}`}>
            {l.type === "sale" ? `${eur(l.pricing.buyer_price)} frais inclus` : `Frais don ${eur(l.pricing.platform_fee)}`}
            {l.pricing.delivery_from != null && ` · livraison dès ${eur(l.pricing.delivery_from)}`}
          </p>
        )}
        <div className="mt-1 flex items-center justify-between text-xs text-stone-500">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" aria-hidden="true" />{l.city}{l.distance_km != null && ` · ${l.distance_km} km`}</span>
          <span>{l.condition}</span>
        </div>
      </div>
    </Link>
  );
}

export function Empty({ children, testid }) {
  return <div data-testid={testid} className="rounded-2xl border border-dashed border-stone-300 p-10 text-center text-stone-500">{children}</div>;
}
